import { useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CalendarDays, Lock, MessageSquare } from "lucide-react";
import { PageHeader, Panel, TagBadge, TAGS, positionsOf, fmt, shortDate } from "../../components/app/kit";
import { DEMO_TEAM, DEMO_GAMES, demoPlayer } from "../../lib/demoData";

// Fictional recruiter, notes and messages for the demo. Players come from the sample team.
const RECRUITER = "Coach Sam Patel, Lakeshore State University (sample)";
const BOARD = [
  ["demo-p1", "Top Target", ["Elite shot prep off the catch; 3 makes from the left corner vs. Lakeview.", "Ask coach about summer camp availability."]],
  ["demo-p5", "Top Target", ["Changes shots at the rim even when he doesn't block them. 10+ rebounds most nights."]],
  ["demo-p2", "Monitoring", ["Runs the offense, 8+ assists. Want to see him against a pressing team."]],
  ["demo-p3", "Monitoring", []],
  ["demo-p8", "Backup", ["Long-term project, class of 2028."]],
].map(([id, tag, notes]) => ({ ...demoPlayer(id), tag, notes }));
const THREAD = [
  ["you", "Hi Coach Rivera, we'd love to see Jordan Ellis play this season. Is the Dec 2 home game a good one to attend?"],
  ["coach", "Absolutely. He's been shooting it well. I'll send his highlight page before then."],
];

const BoardTable = ({ rows, sel, onSel }) => (
  <div className="overflow-x-auto rounded-2xl border border-[#1E293B]">
    <table className="w-full min-w-[600px] text-left text-sm">
      <thead className="bg-[#162032] font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">
        <tr>{["Prospect", "Pos", "Class", "PPG", "GPA", "Notes", "Tag"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <tr key={p.id} data-testid={`demo-board-row-${p.id}`} onClick={() => onSel(p.id)}
            className={`cursor-pointer border-t border-[#1E293B] transition-colors ${sel === p.id ? "bg-[#162032]" : "bg-[#0F172A] hover:bg-[#162032]/70"}`}>
            <td className="px-4 py-3 font-display text-lg font-bold uppercase text-white">{p.name}</td>
            <td className="px-4 py-3 font-mono-gd text-slate-200">{positionsOf(p)}</td>
            <td className="px-4 py-3 font-mono-gd text-slate-200">{p.grad_year}</td>
            <td className="px-4 py-3 font-mono-gd text-[#39FF14]">{fmt(p.season.ppg)}</td>
            <td className="px-4 py-3 font-mono-gd text-slate-200">{fmt(p.gpa)}</td>
            <td className="px-4 py-3 font-mono-gd text-slate-200">{p.notes.length}</td>
            <td className="px-4 py-3"><TagBadge tag={p.tag} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const NotesPanel = ({ p }) => (
  <Panel data-testid="demo-board-notes">
    <p className="font-mono-gd text-[11px] uppercase tracking-[0.2em] text-slate-400">Selected prospect</p>
    <h3 className="font-display text-2xl font-black uppercase text-white">{p.name}</h3>
    <p className="text-sm text-slate-400">{positionsOf(p)} · {DEMO_TEAM.name} · {fmt(p.season.ppg)} PPG · {fmt(p.season.rpg)} RPG · {fmt(p.season.apg)} APG</p>
    <p className="mt-5 flex items-center gap-2 font-mono-gd text-[11px] uppercase tracking-[0.2em] text-[#39FF14]"><Lock className="h-3.5 w-3.5" />Private notes, only you see these</p>
    <ul className="mt-2 space-y-2">
      {p.notes.length ? p.notes.map((n) => <li key={n} className="rounded-xl border border-[#1E293B] bg-[#0A0E1A] p-3 text-sm text-slate-200">{n}</li>)
        : <li className="text-sm text-slate-400">No notes yet.</li>}
    </ul>
    <Link to={`/demo/players/${p.id}`} data-testid="demo-board-open-report" className="mt-5 inline-flex rounded-full bg-[#39FF14] px-5 py-2.5 text-sm font-bold text-[#0A0E1A]">Open full report</Link>
  </Panel>
);

const SidePanels = () => (
  <div className="space-y-6">
    <Panel data-testid="demo-style-alert">
      <h3 className="mb-3 flex items-center gap-2 font-display text-xl font-bold uppercase text-white"><Bell className="h-5 w-5 text-[#39FF14]" />Saved style alert</h3>
      <p className="text-sm text-slate-300">Three-Level Scorer · SG · Class of 2027</p>
      <Link to="/demo/players/demo-p1" className="mt-3 flex items-center justify-between rounded-xl border border-[#39FF14]/40 bg-[#39FF14]/10 p-3 text-sm text-white">
        <span>New match: Jordan Ellis</span><span className="rounded-full bg-[#39FF14] px-2 py-0.5 font-mono-gd text-[10px] font-bold text-[#0A0E1A]">NEW</span>
      </Link>
      <p className="mt-2 text-xs text-slate-400">Recruiters get new matches in a morning email at the time they choose.</p>
    </Panel>
    <Panel data-testid="demo-followed-schedule">
      <h3 className="mb-3 flex items-center gap-2 font-display text-xl font-bold uppercase text-white"><CalendarDays className="h-5 w-5 text-[#39FF14]" />Followed team games</h3>
      {DEMO_GAMES.slice(-3).map((g) => (
        <div key={g.id} className="flex justify-between border-t border-[#1E293B] py-2.5 text-sm first:border-t-0">
          <span className="text-white">{DEMO_TEAM.name} {g.home ? "vs" : "@"} {g.opponent}</span><span className="font-mono-gd text-slate-400">{shortDate(g.date)}</span>
        </div>
      ))}
      <p className="mt-2 text-xs text-slate-400">One calendar link puts every followed team's games on your phone.</p>
    </Panel>
    <Panel data-testid="demo-message-thread">
      <h3 className="mb-3 flex items-center gap-2 font-display text-xl font-bold uppercase text-white"><MessageSquare className="h-5 w-5 text-[#39FF14]" />Messages with the coach</h3>
      <div className="space-y-2">
        {THREAD.map(([who, text]) => (
          <p key={text} className={`max-w-[90%] rounded-2xl px-3 py-2 text-sm ${who === "you" ? "ml-auto bg-[#39FF14] text-[#0A0E1A]" : "bg-[#162032] text-slate-200"}`}>{text}</p>
        ))}
      </div>
      <p className="mt-2 text-xs text-slate-400">Recruiters message coaches, never players or parents directly.</p>
    </Panel>
  </div>
);

export default function DemoRecruiter() {
  const [filter, setFilter] = useState("All");
  const [sel, setSel] = useState(BOARD[0].id);
  const rows = filter === "All" ? BOARD : BOARD.filter((p) => p.tag === filter);
  const selected = BOARD.find((p) => p.id === sel) ?? BOARD[0];
  return (
    <div data-testid="demo-recruiter-page">
      <PageHeader eyebrow={`Sample recruiter view · ${RECRUITER}`} title="Recruiting board" />
      <div className="mb-6 grid grid-cols-3 gap-3">
        {TAGS.map((t) => (
          <div key={t} className="rounded-xl border border-[#1E293B] bg-[#162032] px-4 py-3">
            <TagBadge tag={t} />
            <p data-testid={`demo-board-count-${t.toLowerCase().replace(" ", "-")}`} className="mt-2 font-mono-gd text-3xl font-bold text-white">{BOARD.filter((p) => p.tag === t).length}</p>
          </div>
        ))}
      </div>
      <div className="mb-4 flex gap-1 overflow-x-auto">
        {["All", ...TAGS].map((t) => (
          <button key={t} data-testid={`demo-board-filter-${t.toLowerCase().replace(" ", "-")}`} onClick={() => setFilter(t)}
            className={`whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-semibold transition-colors ${filter === t ? "bg-[#39FF14] text-[#0A0E1A]" : "bg-[#162032] text-slate-300"}`}>{t}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-6"><BoardTable rows={rows} sel={sel} onSel={setSel} /><NotesPanel p={selected} /></div>
        <SidePanels />
      </div>
    </div>
  );
}
